/** */
import React, { useEffect, useState } from 'react';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { SectionCard } from 'app/shared/section-card';
import { Icon } from 'assets/icons';
import { useAutomationJourneyService } from 'hooks/api/automata/journey';
import { useNeuronService } from 'hooks/api/automata/neuron';
import { useAutomationTemplateService } from 'hooks/api/automata/template';
import { useCampaignService } from 'hooks/api/mailing/campaign';
import { useMailActionService } from 'hooks/api/mailing/mailaction';
import { useMailLogService } from 'hooks/api/mailing/maillog';
import { useTransactionalService } from 'hooks/api/mailing/transactional';
import { useSMSService } from 'hooks/api/messaging/sms';
import { useAudienceService } from 'hooks/api/users/audience';
import { useContactService } from 'hooks/api/users/contact';
import { useMobilePushLogService } from 'hooks/messaging/mobile/log';
import { useMobilePushMessageService } from 'hooks/messaging/mobile/message';
import { DomainCard } from './domain-card';
import styles from './statbar.module.css';

const DOMAIN_CARDS = [
  {
    key: 'campaigns',
    title: 'Campaigns',
    icon: 'campaign',
    headline: { metric: 'total_campaigns', label: 'campaigns', format: 'count' },
    supporting: [
      { metric: 'emails_sent', label: 'Emails sent' },
      { metric: 'open_rate', label: 'Open rate', format: 'percent' },
      { metric: 'click_rate', label: 'Click rate', format: 'percent' }
    ]
  },
  {
    key: 'contacts',
    title: 'Contacts',
    icon: 'groups',
    headline: { metric: 'total_contacts', label: 'contacts', format: 'count' },
    supporting: [
      { metric: 'invalid_contacts', label: 'Invalid', danger: true },
      { metric: 'unsubscribed', label: 'Unsubscribed', warn: true },
      { metric: 'total_audiences', label: 'Audiences' }
    ]
  },
  {
    key: 'transactional',
    title: 'Transactional',
    icon: 'transactional',
    headline: { metric: 'total_transactionals', label: 'transactionals', format: 'count' },
    supporting: [
      { metric: 'emails_sent', label: 'Emails sent' },
      { metric: 'open_rate', label: 'Open rate', format: 'percent' },
      { metric: 'click_rate', label: 'Click rate', format: 'percent' }
    ]
  },
  {
    key: 'automation',
    title: 'Automations',
    icon: 'automation',
    headline: { metric: 'total_templates', label: 'templates', format: 'count' },
    supporting: [
      { metric: 'total_journeys', label: 'Journeys' },
      { metric: 'active_neurons', label: 'Active neurons' },
      { metric: 'inactive_neurons', label: 'Inactive', warn: true }
    ]
  },
  {
    key: 'push',
    title: 'Push',
    icon: 'mobile_push',
    headline: { metric: 'total_sends', label: 'sends', format: 'count' },
    supporting: [{ metric: 'total_messages', label: 'Messages' }]
  },
  {
    key: 'sms',
    title: 'SMS',
    icon: 'sms',
    headline: { metric: 'total_sms_sent', label: 'sent', format: 'count' },
    supporting: []
  }
];

const FEATURES = [
  { key: 'campaigns', label: 'Campaigns', domain: 'campaigns', metric: 'total_campaigns' },
  { key: 'contacts', label: 'Contacts', domain: 'contacts', metric: 'total_contacts' },
  { key: 'audiences', label: 'Audiences', domain: 'contacts', metric: 'total_audiences' },
  {
    key: 'transactional',
    label: 'Transactional',
    domain: 'transactional',
    metric: 'total_transactionals'
  },
  { key: 'automation', label: 'Automations', domain: 'automation', metric: 'total_templates' },
  { key: 'journeys', label: 'Journeys', domain: 'automation', metric: 'total_journeys' },
  { key: 'push', label: 'Push', domain: 'push', metric: 'total_sends' },
  { key: 'sms', label: 'SMS', domain: 'sms', metric: 'total_sms_sent' }
];

export const DashboardStatBar = ({ tenant_id }) => {
  const { fetchAudiences } = useAudienceService();
  const { fetchJourneys } = useAutomationJourneyService();
  const { fetchTemplates } = useAutomationTemplateService();
  const { fetchCampaigns } = useCampaignService();
  const { fetchContacts } = useContactService();
  const { fetchMailActions } = useMailActionService();
  const { fetchMailLogs } = useMailLogService();
  const { fetchNeurons } = useNeuronService();
  const { fetchTransactionals } = useTransactionalService();
  const { fetchLogs: fetchPushLogs } = useMobilePushLogService();
  const { fetchMessages: fetchPushMessages } = useMobilePushMessageService();
  const { fetchSMSs } = useSMSService();

  const [campaign_metrics, setCampaignMetrics] = useState({});
  const [contact_metrics, setContactMetrics] = useState({});
  const [transactional_metrics, setTransactionalMetrics] = useState({});
  const [automation_metrics, setAutomationMetrics] = useState({});
  const [push_metrics, setPushMetrics] = useState({});
  const [sms_metrics, setSmsMetrics] = useState({});

  useEffect(() => {
    const q = tenant_id ? `&tenant_id=${tenant_id}` : '';

    Promise.all([
      fetchCampaigns({ query_string: `bool=-is_test_campaign&status=!draft&count=1${q}` }),
      fetchMailLogs({ query_string: `class=campaign&count${q}` }),
      fetchMailActions({ query_string: `resource_type=campaign&action=opened&count=1${q}` }),
      fetchMailActions({ query_string: `resource_type=campaign&action=clicked_link&count=1${q}` })
    ]).then(([{ size: total }, { size: emails_sent }, { size: opened }, { size: clicks }]) => {
      setCampaignMetrics({
        total_campaigns: { value: total },
        emails_sent: { value: emails_sent },
        open_rate: { value: emails_sent > 0 ? (opened / emails_sent) * 100 : 0 },
        click_rate: { value: emails_sent > 0 ? (clicks / emails_sent) * 100 : 0 }
      });
    });

    Promise.all([
      fetchContacts({ query_string: `count=1${q}` }),
      fetchContacts({ query_string: `bool=-is_valid&count=1${q}` }),
      fetchContacts({ query_string: `bool=-is_subscribed&count=1${q}` }),
      fetchAudiences({ query_string: `count=1${q}` })
    ]).then(([{ size: total }, { size: invalid }, { size: unsubscribed }, { size: audiences }]) => {
      setContactMetrics({
        total_contacts: { value: total },
        invalid_contacts: { value: invalid },
        unsubscribed: { value: unsubscribed },
        total_audiences: { value: audiences }
      });
    });

    Promise.all([
      fetchTransactionals({ query_string: `count=1${q}` }),
      fetchMailLogs({ query_string: `class=transactional&count${q}` }),
      fetchMailActions({ query_string: `resource_type=transactional&action=opened&count=1${q}` }),
      fetchMailActions({
        query_string: `resource_type=transactional&action=clicked_link&count=1${q}`
      })
    ]).then(([{ size: total }, { size: emails_sent }, { size: opened }, { size: clicks }]) => {
      setTransactionalMetrics({
        total_transactionals: { value: total },
        emails_sent: { value: emails_sent },
        open_rate: { value: emails_sent > 0 ? (opened / emails_sent) * 100 : 0 },
        click_rate: { value: emails_sent > 0 ? (clicks / emails_sent) * 100 : 0 }
      });
    });

    Promise.all([
      fetchTemplates({ query_string: `count=1${q}` }),
      fetchJourneys({ query_string: `count=1${q}` }),
      fetchNeurons({ query_string: `bool=is_active&count=1${q}` }),
      fetchNeurons({ query_string: `bool=-is_active&count=1${q}` })
    ]).then(([{ size: templates }, { size: journeys }, { size: active }, { size: inactive }]) => {
      setAutomationMetrics({
        total_templates: { value: templates },
        total_journeys: { value: journeys },
        active_neurons: { value: active },
        inactive_neurons: { value: inactive }
      });
    });
    Promise.all([
      fetchPushLogs({ query_string: `count=1${q}` }),
      fetchPushMessages({ query_string: `count=1${q}` })
    ]).then(([{ size: sends }, { size: messages }]) => {
      setPushMetrics({
        total_sends: { value: sends },
        total_messages: { value: messages }
      });
    });

    fetchSMSs({ query_string: `count=1${q}` }).then(({ size: total }) => {
      setSmsMetrics({ total_sms_sent: { value: total } });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id]);

  const domain_metrics = {
    campaigns: campaign_metrics,
    contacts: contact_metrics,
    transactional: transactional_metrics,
    automation: automation_metrics,
    push: push_metrics,
    sms: sms_metrics
  };

  const features = FEATURES.map((f) => ({
    ...f,
    used: (domain_metrics[f.domain]?.[f.metric]?.value || 0) > 0
  }));
  const adopted = features.filter((f) => f.used).length;

  return (
    <>
      <SectionCard title={`Feature Adoption — ${adopted} of ${FEATURES.length} used`}>
        <div className={styles.featureGrid}>
          {features.map((f) => (
            <span
              key={f.key}
              className={`${styles.featurePill} ${
                f.used ? styles.featureUsed : styles.featureUnused
              }`}
            >
              <Icon name={f.used ? 'check' : 'close'} width={10} height={10} />
              {f.label}
            </span>
          ))}
        </div>
      </SectionCard>

      <Spacer multiple={4} />

      <GridRow num_of_columns={2} auto_flow={false}>
        {DOMAIN_CARDS.map((card) => (
          <GridColumn key={card.key} span={1} auto_columns={false}>
            <DomainCard
              title={card.title}
              icon={card.icon}
              headline={card.headline}
              supporting={card.supporting}
              metrics={domain_metrics[card.key]}
            />
          </GridColumn>
        ))}
      </GridRow>
    </>
  );
};
